import { useEffect, useRef, useState } from "react";
import { ArrowUp } from "lucide-react";
import { Link } from "react-router-dom";
import { ArticleContent } from "@/components/ArticleContent";
import { ArticleReaderTools } from "@/components/ArticleReaderTools";
import { ArticleSequentialNavigation } from "@/components/ArticleSequentialNavigation";
import { ContinueResearching } from "@/components/ContinueResearching";
import { LvmhAtAGlance, LvmhPrelude } from "@/components/LvmhEditorial";
import { HsbcFrontMatter } from "@/components/HsbcFrontMatter";
import { LicensedPhotograph } from "@/components/LicensedPhotograph";
import { PageMeta } from "@/components/PageMeta";
import { ReadingProgress } from "@/components/ReadingProgress";
import { TableOfContents } from "@/components/TableOfContents";
import { AtAGlance } from "@/components/StrategyFigures";
import { ComplexityCover } from "@/components/ComplexityCover";
import { readerScrollBehavior } from "@/lib/scrollBehavior";
import { focusTarget } from "@/lib/motion";
import { formatPublicationDate, getReadTime } from "@/content/researchArticles";

const CANONICAL_ORIGIN = process.env.REACT_APP_CANONICAL_ORIGIN;

export const ArticleLayout = ({ article, previous, next }) => {
  const articleRef = useRef(null);
  const bodyRef = useRef(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const archivePath = article.archivePath ?? "/research";
  const archiveLabel = article.archiveLabel ?? "Company Research";
  const articleUrl = `${CANONICAL_ORIGIN}${article.path}`;
  const socialImage = `${CANONICAL_ORIGIN}${article.hero.socialSrc ?? article.hero.src}`;
  const readTime = getReadTime(article);
  const authorSchema = article.authorType === "Organization"
    ? { "@type": "Organization", "@id": `${CANONICAL_ORIGIN}/#organization`, name: article.author, url: `${CANONICAL_ORIGIN}/` }
    : { "@type": "Person", "@id": `${CANONICAL_ORIGIN}/about#founder`, name: article.author, url: `${CANONICAL_ORIGIN}/about` };
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "@id": `${articleUrl}#article`,
        headline: article.title,
        description: article.description,
        mainEntityOfPage: { "@type": "WebPage", "@id": articleUrl },
        image: [socialImage],
        datePublished: article.publishedAt,
        dateModified: article.modifiedAt,
        author: authorSchema,
        publisher: { "@id": `${CANONICAL_ORIGIN}/#organization` },
        articleSection: article.category,
        ...(article.tags ? { keywords: article.tags } : {}),
        inLanguage: "en-AU",
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${CANONICAL_ORIGIN}/` },
          { "@type": "ListItem", position: 2, name: archiveLabel, item: `${CANONICAL_ORIGIN}${archivePath}` },
          { "@type": "ListItem", position: 3, name: article.title, item: articleUrl },
        ],
      },
    ],
  };

  useEffect(() => {
    const updateVisibility = () => setShowBackToTop(window.scrollY > window.innerHeight * 0.85);
    updateVisibility();
    window.addEventListener("scroll", updateVisibility, { passive: true });
    return () => window.removeEventListener("scroll", updateVisibility);
  }, []);

  return <>
    <PageMeta
      title={article.seoTitle ?? `${article.title} | FU Research`}
      description={article.description}
      path={article.path}
      type="article"
      image={socialImage}
      imageAlt={article.hero.alt}
      publishedAt={article.publishedAt}
      modifiedAt={article.modifiedAt}
      schema={schema}
    />
    <ReadingProgress articleRef={bodyRef} />
    <article ref={articleRef} className={`research-article ${article.hero.kind === "framework" ? "strategy-article" : ""} ${article.layoutVariant ?? ""}`} data-testid={`research-article-${article.slug}`}>
      <header className="article-header shell" data-testid="article-header">
        <Link to={archivePath} className="article-back-link" data-testid="article-back-to-archive-link">← {archiveLabel} archive</Link>
        <p className="eyebrow" data-testid="article-category">{article.category}{article.topic ? ` ${article.categorySeparator ?? "·"} ${article.topic}` : ""}</p>
        <h1 data-motion="title" data-testid="article-title">{article.title}</h1>
        {article.subtitle && <p className="article-subtitle" data-motion="metadata" data-testid="article-subtitle">{article.subtitle}</p>}
        {article.deck && <p className="article-dek" data-motion="metadata" data-testid="article-deck">{article.deck}</p>}
        <span className="article-author" data-motion="metadata" data-testid="article-author">By <Link to={article.authorPath ?? "/about"} data-testid="article-author-link">{article.author}</Link>{article.publisherLabel ? ` | ${article.publisherLabel}` : ""}</span>
        <div className="article-header-side" data-motion="metadata">
        <div className={`article-byline ${article.modifiedAt !== article.publishedAt ? "has-update" : ""}`} data-testid="article-publication-metadata">
          <time dateTime={article.publishedAt} data-testid="article-publication-date">{formatPublicationDate(article.publishedAt)}</time>
          {article.modifiedAt !== article.publishedAt && <time dateTime={article.modifiedAt} data-testid="article-updated-date">Updated {formatPublicationDate(article.modifiedAt)}</time>}
          <span data-testid="article-read-time">{readTime}</span>
        </div>
        <ArticleReaderTools article={article} bodyRef={bodyRef} />
        </div>
      </header>

      <div data-motion="body" data-testid="article-content-entrance">
      {article.hero.license && <LicensedPhotograph photo={article.hero} testId="article-hero-figure" hero />}
      {article.hero.kind !== "framework" && !article.hero.license && <figure className="article-hero-media shell" data-testid="article-hero-figure">
        <div className="article-hero-image-frame" style={{ backgroundColor: article.hero.background }} data-testid="article-hero-image-frame">
          <img src={article.hero.src} alt={article.hero.alt} loading="eager" style={{ objectFit: article.hero.fit, objectPosition: article.hero.position }} data-testid="article-hero-image" />
        </div>
        <figcaption data-testid="article-hero-caption">
          <span>{article.hero.caption}</span>
          <a href={article.hero.source} target="_blank" rel="noreferrer" data-testid="article-hero-source-link">Image: {article.hero.credit} ↗</a>
        </figcaption>
      </figure>}

      {article.hero.editorialSrc && <ComplexityCover hero={article.hero} />}
      {article.frontMatter && <HsbcFrontMatter content={article.frontMatter} />}
      {article.prelude && <LvmhPrelude section={article.prelude} />}
      {article.atAGlance && (article.atAGlance.variant === "lvmh" ? <LvmhAtAGlance content={article.atAGlance} /> : <AtAGlance content={article.atAGlance} />)}

      {article.researchQuestion && article.keyFinding && <section className="research-summary shell" aria-labelledby="research-summary-title" data-testid="article-research-summary">
        <p className="research-summary-kicker" id="research-summary-title" data-testid="research-summary-label">Research summary</p>
        <div data-testid="research-summary-question"><span>Research question</span><p>{article.researchQuestion}</p></div>
        <div data-testid="research-summary-finding"><span>Key finding</span><p>{article.keyFinding}</p></div>
      </section>}

      <div ref={bodyRef} className="article-reading-layout shell" data-testid="article-reading-layout">
        <TableOfContents sections={article.sections.filter((section) => !section.isSupplementary)} waitForCollapse={article.contents?.waitForCollapse} />
        <ArticleContent article={article} />
      </div>

      {article.tags && <ul className="article-tags shell" aria-label="Article topics" data-testid="article-tags">{article.tags.map((tag) => <li key={tag} data-testid={`article-tag-${tag.toLowerCase().replaceAll(" ", "-")}`}>{tag}</li>)}</ul>}
      {article.showSequentialNavigation && <ArticleSequentialNavigation previous={previous} next={next} />}
      <ContinueResearching article={article} />
      <div className="article-end shell" data-testid="article-end-section">
        <p data-testid="article-end-label">{article.endLabel ?? "End of research note"}</p>
        <Link to={archivePath} data-testid="article-return-to-archive-link">Return to {archiveLabel} <span aria-hidden="true">↗</span></Link>
      </div>
      </div>
    </article>
    <button
      className={`back-to-top ${showBackToTop ? "is-visible" : ""}`}
      type="button"
      aria-label="Back to top"
      title="Back to top"
      onClick={() => { focusTarget(articleRef.current?.querySelector("h1")); window.scrollTo({ top: 0, behavior: readerScrollBehavior() }); }}
      data-testid="article-back-to-top-button"
    ><ArrowUp size={17} aria-hidden="true" /></button>
  </>;
};